import React, { useState, useRef, useEffect } from "react";
import { Button, Table, Modal } from "react-bootstrap";
import moment from "moment";
import { get, groupBy } from "lodash";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import core from "../../../vendors/core-api";
import { setPreferredTableNumber } from "../../../partials/reservations/reservationsApi";
import ServiceChart from "./ServiceChart";
import GuestInformationModal from "./GuestInformationModal";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import {
  DB_DATE_FORMAT,
  DB_HOURS_FORMAT,
  RESERVATION_COLOR,
} from "../../../constants";
import {
  getCalendarEventProps,
  getReservationHighlightColor,
} from "../../../helpers/reservations";
import ReservationDot from "../../../partials/reservations/ReservationDot";
import ReservationLegend from "../../../partials/reservations/ReservationLegend";

/**
 * Formats a Date object or date string into YYYY-MM-DD format.
 * @param {Date | string} date The date to format.
 * @returns {string} Formatted date string.
 */
const formatDateForInput = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const SUB_PAGES = {
  PREP_SUMMARY: "Prep Summary",
  SERVICE_CHART: "Service chart",
};

// CONSTANT: Defined column widths used in the ColGroup helper
// Using percentages ensures they fill the width, fixed pixels for small columns
const COL_WIDTHS = {
  DOT: "5%",
  NAME: "25%",
  NOTES: "25%",
  CELEBS: "25%",
  SIZE: "5%",
  TABLE_NAME: "10%",
  TABLE_NO: "5%",
};

// Helper component to ensure identical column sizing across all tables
const TableColGroup = () => (
  <colgroup>
    <col style={{ width: COL_WIDTHS.DOT }} />
    <col style={{ width: COL_WIDTHS.NAME }} />
    <col style={{ width: COL_WIDTHS.NOTES }} />
    <col style={{ width: COL_WIDTHS.CELEBS }} />
    <col style={{ width: COL_WIDTHS.SIZE }} />
    <col style={{ width: COL_WIDTHS.TABLE_NAME }} />
    <col style={{ width: COL_WIDTHS.TABLE_NO }} />
  </colgroup>
);

const Headers = ({
  reservations,
  store,
  selectedDate,
  handleShowDatepickerModal,
}) => {
  const totalReservations = reservations.length;
  const totalPartySize = reservations.reduce((total, res) => {
    const guest_count = res.order_details
      .filter((detail) => detail.item.type === "reservation_experience")
      .reduce((acc, cur) => acc + cur.qty, 0);
    return total + guest_count;
  }, 0);

  const formattedHeaderDate = moment(selectedDate, DB_DATE_FORMAT).format(
    "ddd MMM D, YYYY",
  );

  const dayOfWeek = moment(selectedDate, DB_DATE_FORMAT).format("d");
  const storeHoursToday = store?.store_hours?.find(
    (h) => h.day === dayOfWeek && h.isOpen,
  );

  const storeHoursDisplay = storeHoursToday
    ? `${moment(storeHoursToday.opening, "HH:mm").format("h:mm A")} - ${moment(
        storeHoursToday.closing,
        "HH:mm",
      ).format("h:mm A")}`
    : "Closed";

  return (
    <div className="d-flex justify-content-between align-items-end p-3 border-bottom border-dark mb-3 d-print-none">
      <div style={{ width: "20%" }}>
        <h5 className="fw-bold mb-0">{store?.name}</h5>
      </div>
      <div style={{ width: "20%" }} className="text-center">
        <h6 className="mb-0 cursor-pointer" onClick={handleShowDatepickerModal}>
          {formattedHeaderDate}
          <i className="far fa-calendar-alt text-muted ml-2 fa-lg"></i>
        </h6>
      </div>
      <div style={{ width: "20%" }} className="text-center">
        <h6 className="mb-0 fw-bold">{storeHoursDisplay}</h6>
      </div>
      <div style={{ width: "5%" }} className="text-end">
        <h6 className="mb-0 fw-bold">
          <i className="fa fa-calendar-check mr-1" />
          {totalReservations}
        </h6>
      </div>
      <div style={{ width: "5%" }} className="text-end">
        <h6 className="mb-0 fw-bold">
          <i className="fa fa-user mr-1" />
          {totalPartySize}
        </h6>
      </div>
    </div>
  );
};

const PrepSummary = ({
  reservations,
  onOptimisticUpdate,
  onCustomerNameClick,
  store,
  handleShowDatepickerModal,
  selectedDate,
}) => {
  const [editableRow, setEditableRow] = useState(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const printRef = useRef();

  const purpleLinkStyle = {
    color: "#8950fc",
    textDecoration: "underline",
    cursor: "pointer",
    fontWeight: "bold",
  };

  const handleDownloadPdf = () => {
    setIsPrinting(true);
  };

  useEffect(() => {
    if (isPrinting) {
      const generatePdf = async () => {
        const element = printRef.current;
        if (!element) {
          setIsPrinting(false);
          return;
        }

        const originalFontSize = element.style.fontSize;
        element.style.fontSize = "125%";

        try {
          const canvas = await html2canvas(element, {
            useCORS: true,
            scale: 2,
          });

          const imgData = canvas.toDataURL("image/png");
          const pdf = new jsPDF({
            orientation: "portrait",
            unit: "mm",
            format: "a4",
          });

          const pdfWidth = pdf.internal.pageSize.getWidth();
          const pdfHeight = pdf.internal.pageSize.getHeight();
          const imgWidth = canvas.width;
          const imgHeight = canvas.height;
          const marginX = 10;
          const marginY = 15;
          const contentWidth = pdfWidth - marginX * 2;
          const contentHeight = pdfHeight - marginY * 2;
          const ratio = Math.min(
            contentWidth / imgWidth,
            contentHeight / imgHeight,
          );

          pdf.addImage(
            imgData,
            "PNG",
            marginX,
            marginY,
            imgWidth * ratio,
            imgHeight * ratio,
          );
          pdf.save(`prep-summary-${moment().format("YYYY-MM-DD")}.pdf`);
        } finally {
          element.style.fontSize = originalFontSize;
          setIsPrinting(false);
        }
      };
      generatePdf();
    }
  }, [isPrinting]);

  const api = core("pu").get();

  const handleTableNumberUpdate = async (reservationId, newTableNumber) => {
    const originalReservations = JSON.parse(JSON.stringify(reservations));
    const reservationToUpdate = originalReservations.find(
      (r) => r._id === reservationId,
    );
    const originalTableNumber =
      reservationToUpdate?.reservation_notes?.preferred_table_number || "";

    // Optimistic update
    onOptimisticUpdate(reservationId, newTableNumber);
    setEditableRow(null);

    try {
      await setPreferredTableNumber(api, reservationId, newTableNumber);
    } catch (err) {
      onOptimisticUpdate(reservationId, originalTableNumber);
    }
  };

  const sortedReservations = [...reservations].sort((a, b) =>
    moment(a.order_time, DB_HOURS_FORMAT).diff(
      moment(b.order_time, DB_HOURS_FORMAT),
    ),
  );
  const groupedByTime = groupBy(sortedReservations, "order_time");

  // Custom style for thick borders
  const thickBorderStyle = { border: "2px solid #000" };

  return (
    <>
      <div className="d-flex justify-content-end pb-2 d-print-none">
        <Button
          onClick={handleDownloadPdf} // This button is for PDF download, not printing directly
          variant="info"
          disabled={isPrinting}
        >
          {isPrinting ? "Generating..." : "Download Summary"}
        </Button>
      </div>
      <div ref={printRef} className="printable-content">
        {" "}
        {/* Added printable-content class for targeting */}
        <ReservationLegend className="mb-3" />
        <Headers
          reservations={reservations}
          store={store}
          selectedDate={selectedDate}
          handleShowDatepickerModal={handleShowDatepickerModal}
        />
        {/* GLOBAL HEADER TABLE 
        Using a <table> here instead of a <div> ensures the headers align 
        perfectly with the data tables below because they share the same <colgroup>.
      */}
        <div className="mb-2">
          <Table
            bordered
            size="sm"
            className="mb-0"
            style={{ tableLayout: "fixed" }}
          >
            <TableColGroup />
            <thead className="thead-light">
              <tr>
                <th className="border-0"></th>
                <th>Name</th>
                <th>Allergies/Preferences</th>
                <th>Celebrations</th>
                <th className="text-center">Party</th>
                <th>Table</th>
                <th className="text-center">#</th>
              </tr>
            </thead>
          </Table>
        </div>
        {Object.entries(groupedByTime).map(([time, reservationsInGroup]) => {
          const totalPax = reservationsInGroup.reduce((acc, res) => {
            const guestCount = res.order_details
              .filter((detail) => detail.item.type === "reservation_experience")
              .reduce((subAcc, cur) => subAcc + cur.qty, 0);
            return acc + guestCount;
          }, 0);

          const firstReservation = reservationsInGroup[0];
          const diningInterval = get(
            firstReservation,
            "reservation_notes.dining_service.interval",
            60,
          );
          const endTime = moment(time, DB_HOURS_FORMAT)
            .add(diningInterval, "minutes")
            .format("HH:mm");
          const timeTitle = `${moment(time, DB_HOURS_FORMAT).format(
            "HH:mm",
          )} - ${endTime}`;

          return (
            <div key={time} className="mb-4">
              <div
                className="fw-bold mb-1"
                style={{
                  fontSize: "1em",
                  color: "#000",
                  paddingLeft: "5px",
                }}
              >
                {timeTitle}
                <span className="ml-3">
                  <i className="fa fa-calendar-check mr-1" />
                  {reservationsInGroup.length}
                  <i className="fa fa-user ml-2 mr-1" />
                  {totalPax}
                </span>
              </div>

              {/* INDIVIDUAL TIME GROUP TABLE */}
              <Table
                bordered
                size="sm"
                style={{
                  tableLayout: "fixed",
                  ...thickBorderStyle, // Applies the thicker outer border
                }}
                className="border-dark" // Bootstrap class for darker borders
              >
                <TableColGroup />
                <tbody>
                  {reservationsInGroup.map((reservation) => {
                    const { guest_customer, order_details, notes } =
                      reservation;
                    const customer_name =
                      guest_customer.last_name && guest_customer.first_name
                        ? `${guest_customer.title || ""} ${
                            guest_customer.first_name
                          } ${guest_customer.last_name}`.trim()
                        : "Walk-in";
                    const parsedNotes = notes ? JSON.parse(notes) : {};
                    const dietary = get(parsedNotes, "dietary", []);
                    const celebrations = get(parsedNotes, "celebration", []);
                    const celebrationLabels = celebrations
                      .map((d) => `${d.label}`)
                      .join(", ");
                    const dietaryLabels = dietary
                      .map((d) => `${d.count}p ${d.label}`)
                      .join(", ");
                    const celebrationNotes =
                      celebrationLabels || "No celebrations";
                    const displayNotes = dietaryLabels || "No allergies";
                    const isPdr =
                      reservation.reservation_notes?.table?.isExclusive;
                    const hasDietary = dietary.length > 0;
                    const color = getReservationHighlightColor({
                      isVip: reservation.partner_identifier?.level === "VIP",
                      isPaid: reservation.payment?.amount > 0,
                      hasDietary,
                      isPendingPayment:
                        reservation.payment?.status === "pending" ||
                        reservation.payment?.status === "failed",
                    });

                    const guest_count = order_details
                      .filter(
                        (detail) =>
                          detail.item.type === "reservation_experience",
                      )
                      .reduce((acc, cur) => acc + cur.qty, 0);

                    const table = order_details.find(
                      (detail) => detail.item.type === "reservation",
                    );

                    const tableNumber =
                      reservation.reservation_notes.preferred_table_number ||
                      "";

                    return (
                      <tr
                        key={reservation._id}
                        style={{ backgroundColor: "#fff" }}
                      >
                        {/* Status Dot */}
                        <td className="align-middle text-center border-dark">
                          <ReservationDot color={color} isExclusive={isPdr} />
                        </td>

                        {/* Name */}
                        <td
                          className="align-middle border-dark"
                          style={isPrinting ? {} : purpleLinkStyle}
                          onClick={() => onCustomerNameClick(reservation)}
                        >
                          <a
                            style={
                              isPrinting ? { color: "#000" } : purpleLinkStyle
                            }
                          >
                            {customer_name}
                          </a>
                        </td>

                        {/* Notes */}
                        <td
                          className="align-middle border-dark"
                          style={hasDietary ? { color: "#D90429" } : undefined}
                        >
                          {displayNotes}
                        </td>

                        <td className="align-middle border-dark">
                          {celebrationNotes}
                        </td>

                        {/* Size */}
                        <td className="align-middle text-center border-dark">
                          {guest_count}p
                        </td>

                        {/* Table Name */}
                        <td
                          className="align-middle border-dark"
                          style={isPdr ? { color: "#673ab7" } : {}}
                        >
                          {table?.item.name || ""}
                        </td>

                        {/* Table Number (Input) */}
                        <td
                          onClick={() => setEditableRow(reservation._id)}
                          className="align-middle text-center border-dark"
                          style={{ cursor: "pointer" }}
                        >
                          {editableRow === reservation._id ? (
                            <input
                              type="text"
                              defaultValue={tableNumber}
                              onBlur={(e) =>
                                handleTableNumberUpdate(
                                  reservation._id,
                                  e.target.value,
                                )
                              }
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  handleTableNumberUpdate(
                                    reservation._id,
                                    e.target.value,
                                  );
                                }
                              }}
                              autoFocus
                              className="form-control form-control-sm p-0 text-center"
                              style={{ height: "24px" }}
                            />
                          ) : (
                            <div style={{ minHeight: "20px" }}>
                              {tableNumber}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          );
        })}
      </div>
    </>
  );
};

const ReservationsOverview = ({
  reservations,
  onGoingService,
  storeName,
  selectedDate,
  onDateInputChange,
  store,
  onGuestReservationClick,
  onOptimisticUpdate,
}) => {
  const [subPage, setSubPage] = useState(SUB_PAGES.PREP_SUMMARY);
  const [showDatepickerModal, setShowDatepickerModal] = useState(false);
  const calendarRefOverview = useRef(null);
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [showGuestModal, setShowGuestModal] = useState(false);
  const api = core("pu").get();

  useEffect(() => {
    if (showDatepickerModal) {
      // Scroll to the selected date when the modal opens
      setTimeout(() => {
        const selectedDateElement = document.querySelector(
          ".selected-reservation-date",
        );
        selectedDateElement?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 200); // A small delay to ensure the calendar has rendered
    }
  }, [showDatepickerModal]);

  const handleCustomerNameClick = (reservation) => {
    setSelectedGuest(reservation.partner_identifier);
    setShowGuestModal(true);
  };

  const handleShowDatepickerModal = () => {
    setTimeout(() => {
      calendarRefOverview.current?.getApi().updateSize();
    }, 100);
    setShowDatepickerModal(true);
  };

  const handleCloseDatepickerModal = () => {
    setShowDatepickerModal(false);
  };

  const fetchCalendarReservations = async (fetchInfo, successCallback) => {
    const store_id = reservations[0]?.store;
    if (!store_id) {
      successCallback([]);
      return;
    }
    const start = moment(fetchInfo.startStr).format(DB_DATE_FORMAT);
    const end = moment(fetchInfo.endStr).format(DB_DATE_FORMAT);

    try {
      const { data } = await api.get(
        `/reservation/store/${store_id}/date-range?startDate=${start}&endDate=${end}`,
      );

      const events = Object.values(data)
        .flat()
        .filter((res) => !res.status?.toLowerCase().includes("cancelled"))
        .map(getCalendarEventProps);

      successCallback(events);
    } catch (error) {
      console.error("Failed to fetch calendar reservations:", error);
    }
  };

  const dayHeaderContent = (arg) => {
    const date = moment(arg.date).format(DB_DATE_FORMAT);
    const dayReservations = reservations?.filter(
      (r) => moment(r.order_date).format(DB_DATE_FORMAT) === date,
    );
    if (!dayReservations || dayReservations.length === 0)
      return arg.dayNumberText;
    const totalPax = dayReservations.reduce((acc, res) => {
      const guestCount = res.order_details
        ? res.order_details
            .filter((detail) => detail.item?.type === "reservation_experience")
            .reduce((subAcc, cur) => subAcc + (cur.qty || 0), 0)
        : res.guest_count || 0;
      return acc + guestCount;
    }, 0);
    return (
      <div>
        <div>{arg.dayNumberText}</div>
        <div
          style={{ fontSize: "0.7em" }}
        >{`${dayReservations.length} (${totalPax})`}</div>
      </div>
    );
  };

  return (
    <>
      <style>
        {`
          .bg-prepaid {
            background-color: #198754 !important;
          }
        .bg-orange {
          background-color: #fa903e !important;
        }
          @media print {
            @page {
              size: A4 portrait;
              margin: 0;
            }
            body * {
              visibility: hidden;
            }
            .printable-area, .printable-area * {
              visibility: visible;
            }
            .printable-area {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              height: auto;
              overflow: visible;
              padding: 0 !important;
              margin: 0 !important;
            }
            .overview-scroll-area {
              height: auto !important;
              overflow: visible !important;
            }
          }
        `}
      </style>
      <div className="d-flex flex-column h-100 printable-area">
        <div className="d-flex d-print-none">
          {Object.values(SUB_PAGES).map((page) => (
            <Button
              key={page}
              variant={subPage === page ? "info" : "light"}
              onClick={() => setSubPage(page)}
              className="m-2"
            >
              {page}
            </Button>
          ))}
        </div>

        <div
          className="px-3 overview-scroll-area"
          style={{ flex: "1 1 auto", minHeight: 0, overflowY: "auto" }}
        >
          {subPage === SUB_PAGES.SERVICE_CHART && (
            <ServiceChart
              reservations={reservations}
              store={store}
              selectedDate={selectedDate}
              handleShowDatepickerModal={handleShowDatepickerModal}
              onCustomerNameClick={handleCustomerNameClick}
              onGoingService={onGoingService}
              headers={
                <Headers
                  reservations={reservations}
                  store={store}
                  selectedDate={selectedDate}
                  handleShowDatepickerModal={handleShowDatepickerModal}
                />
              }
            />
          )}
          {subPage === SUB_PAGES.PREP_SUMMARY && (
            <PrepSummary
              selectedDate={selectedDate}
              reservations={reservations}
              store={store}
              onCustomerNameClick={handleCustomerNameClick}
              onOptimisticUpdate={onOptimisticUpdate}
              handleShowDatepickerModal={handleShowDatepickerModal}
            />
          )}
        </div>
      </div>

      {selectedGuest && (
        <GuestInformationModal
          show={showGuestModal}
          onHide={() => setShowGuestModal(false)}
          guestId={selectedGuest._id}
          api={api}
          onReservationClick={onGuestReservationClick}
        />
      )}

      <Modal
        dialogClassName="modal-90w"
        size="xl"
        show={showDatepickerModal}
        onHide={handleCloseDatepickerModal}
        centered
      >
        <style>
          {`
              .modal-90w {
                max-width: 70vw !important;
              }
              .selected-reservation-date {
                border: 2px solid ${RESERVATION_COLOR} !important;
                font-weight: bold;
                border-radius: 4px; /* Optional: for rounded corners */
              }
            `}
        </style>
        <Modal.Header closeButton>
          <Modal.Title>Select a Date</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="events-calendar-wrapper">
            <FullCalendar
              className="events-calendar-content"
              ref={calendarRefOverview}
              plugins={[dayGridPlugin, interactionPlugin]}
              initialView="dayGridMonth"
              initialDate={selectedDate}
              events={fetchCalendarReservations}
              eventClick={(clickInfo) => {
                clickInfo.jsEvent.preventDefault();
                onDateInputChange({
                  target: { value: formatDateForInput(clickInfo.event.start) },
                });
                handleCloseDatepickerModal();
              }}
              dateClick={(arg) => {
                arg.jsEvent.preventDefault();
                onDateInputChange({
                  target: { value: arg.dateStr },
                });
                handleCloseDatepickerModal();
              }}
              height="auto"
              dayCellClassNames={(arg) => {
                const cellDate = formatDateForInput(arg.date);
                if (cellDate === formatDateForInput(selectedDate)) {
                  return ["selected-reservation-date"];
                }
                return [];
              }}
              titleFormat={{
                month: "long",
                year: "numeric",
              }}
              dayMaxEvents={6}
              dayCellContent={dayHeaderContent}
            />
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default ReservationsOverview;
